import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import projectsSnapshot from '../data/hero-projects.json';

// The snapshot is built at deploy time by scripts/snapshot-projects.js, which
// also downloads each thumbnail to public/hero-thumbs/. So projects + image
// paths ship inside the bundle — no Firestore call, no firebasestorage hop.
// New projects added in the CMS after a deploy will appear at runtime via the
// background fetch below; existing ones keep their bundled local thumbnails.

let projectsPromise = null;
const fetchProjectsOnce = () => {
  if (projectsPromise) return projectsPromise;
  projectsPromise = getDocs(collection(db, 'projects'))
    .then(snap => snap.docs.map(doc => ({
      id: doc.id,
      title: doc.data().title || '',
      image: doc.data().thumbnails?.[0] || doc.data().images?.[0] || '',
    })))
    .catch(() => []);
  return projectsPromise;
};

const HeroSection = styled.section`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bg};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 56px;
  padding-top: 64px;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 80px 20px 60px;
    min-height: auto;
  }
`;

const Frame = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 40px 0 80px;
`;

const HeroTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;

  @media (max-width: 960px) {
    flex-direction: column;
    gap: 32px;
  }
`;

const HeroLeft = styled.div`
  max-width: 580px;
  width: 100%;
`;

const HeroTitle = styled(motion.h1)`
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: clamp(36px, 4.2vw, 62px);
  font-weight: 500;
  line-height: 0.98;
  letter-spacing: -0.04em;
  margin: 0 0 22px;
  color: ${({ theme }) => theme.colors.text.primary};

  em {
    font-style: italic;
    color: #FF8A6B;
  }
`;

const HeroSub = styled(motion.p)`
  font-size: 17px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 520px;
  margin: 0 0 28px;
`;

/* ── Cloud scatter ── */

const CloudWrap = styled(motion.div)`
  flex-shrink: 0;
  overflow: visible;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const CloudContainer = styled.div`
  position: relative;
  width: 460px;
  height: 400px;
  overflow: visible;

  @media (max-width: 600px) {
    width: 100%;
    height: 320px;
  }
`;

const IconWrap = styled.div`
  position: absolute;

  &:hover .icon-tooltip {
    opacity: 1;
    transform: translateX(-50%) translateY(-2px);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%) translateY(0);
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 3px 8px;
  font-family: 'DM Mono', monospace;
  font-size: 9px;
  white-space: nowrap;
  color: ${({ theme }) => `rgba(${theme.colors.textRgb},0.75)`};
  pointer-events: none;
  opacity: 0;
  transition: opacity 120ms ease, transform 120ms ease;
  z-index: 100;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FALLBACK_COLORS = [
  '#1C2332', '#1A2420', '#261C2A', '#28200E', '#1C2828',
  '#281E0E', '#1E1C2E', '#1A2820', '#2A1C1E', '#1C1E2A',
];

// Varied sizes for depth — cycles through 16 values
const CIRCLE_SIZES = [84, 68, 80, 72, 88, 76, 68, 84, 76, 72, 80, 68, 84, 76, 80, 72];

const IconCircle = styled(motion.button)`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.1)`};
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg || '#1A1A1E'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DM Mono', monospace;
  font-size: ${({ $size }) => Math.round($size * 0.13)}px;
  font-weight: 500;
  color: ${({ theme }) => `rgba(${theme.colors.textRgb},0.4)`};
  transition: border-color 140ms ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: rgba(255,91,58,0.6);
    z-index: 10;
    position: relative;
  }

  &:focus {
    outline: none;
  }
`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: d, ease: [0.16, 1, 0.3, 1] } }),
};

// Golden-angle spiral — organic scatter, never a grid
const GOLDEN_DEG = 137.508;
const getCloudPos = (idx, total, radius = 240) => {
  const angle = idx * GOLDEN_DEG * (Math.PI / 180);
  const r = radius * Math.sqrt((idx + 0.5) / Math.max(total, 1));
  return {
    x: Math.cos(angle) * r,
    y: Math.sin(angle) * r * 0.60,
  };
};

// Independent X + Y drift per circle
const floatFor = (idx) => ({
  x: [0, ((idx % 3) - 1) * 3, 0],
  y: [0, -(4 + (idx % 5) * 2.2), 0],
  duration: 2.6 + (idx % 9) * 0.28,
  delay: (idx * 0.31) % 3.4,
});

const Hero = () => {
  const navigate = useNavigate();
  // Initial state from the build-time snapshot — synchronous, with local
  // thumbnail paths, so the cloud paints with real images on first frame.
  const [projects, setProjects] = useState(() => projectsSnapshot || []);

  // Pick up projects added in the CMS after the last deploy, but preserve
  // bundled local thumbnails for projects already in the snapshot.
  useEffect(() => {
    let alive = true;
    fetchProjectsOnce().then(fresh => {
      if (!alive || !fresh?.length) return;
      setProjects(prev => {
        const byId = new Map(prev.map(p => [p.id, p]));
        let changed = false;
        for (const f of fresh) {
          if (!byId.has(f.id)) { byId.set(f.id, f); changed = true; }
        }
        return changed ? Array.from(byId.values()) : prev;
      });
    });
    return () => { alive = false; };
  }, []);

  // Inject <link rel="preload" as="image"> for the first batch of icons so
  // the browser starts the image fetch in parallel with React's first render.
  useEffect(() => {
    if (!projects.length || typeof document === 'undefined') return;
    const links = projects
      .slice(0, 8)
      .filter(p => p.image)
      .map(p => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = p.image;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
        return link;
      });
    return () => { links.forEach(l => l.remove()); };
  }, [projects]);

  const containerRef = useRef(null);
  const isMobileInit = typeof window !== 'undefined' && window.innerWidth <= 600;
  const [containerW, setContainerW] = useState(isMobileInit ? 320 : 460);
  const [containerH, setContainerH] = useState(isMobileInit ? 320 : 400);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => { setContainerW(el.offsetWidth); setContainerH(el.offsetHeight); };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [projects.length]); // re-run when CloudContainer actually mounts

  const isSmall    = containerW <= 480;
  const cloudCenterX = containerW / 2;
  const cloudCenterY = containerH / 2;
  const cloudRadius  = isSmall ? 190 : 260;
  const sizeScale    = isSmall ? 0.80 : 1;

  return (
    <HeroSection id="hero">
      <Frame>
        <HeroTop>
          <HeroLeft>
            <HeroTitle variants={fadeUp} initial="hidden" animate="show" custom={0}>
              Quality <em>apps</em>,<br />built by <em>engineers</em>,<br />accelerated by <em>AI</em>.
            </HeroTitle>

            <HeroSub variants={fadeUp} initial="hidden" animate="show" custom={0.2}>
              Delivered fast. Built right. Scaled with confidence.
            </HeroSub>
          </HeroLeft>

          {projects.length > 0 && (
            <CloudWrap
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >

              <CloudContainer ref={containerRef}>
                {projects.map((proj, idx) => {
                  const f = floatFor(idx);
                  const pos = getCloudPos(idx, projects.length, cloudRadius);
                  const size = Math.round(CIRCLE_SIZES[idx % CIRCLE_SIZES.length] * sizeScale);
                  const left = cloudCenterX + pos.x - size / 2;
                  const top  = cloudCenterY + pos.y - size / 2;
                  return (
                    <IconWrap key={proj.id} style={{ left, top, zIndex: idx + 1 }}>
                      <IconCircle
                        $size={size}
                        $bg={FALLBACK_COLORS[idx % FALLBACK_COLORS.length]}
                        onClick={() => navigate(`/project/${proj.id}`)}
                        animate={{ x: f.x, y: f.y }}
                        transition={{
                          duration: f.duration,
                          delay: f.delay,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                        whileHover={{
                          scale: 1.3,
                          x: 0,
                          y: 0,
                          transition: { type: 'spring', stiffness: 400, damping: 18 },
                        }}
                        whileTap={{ scale: 0.92 }}
                      >
                        {proj.image
                          ? <img
                              src={proj.image}
                              alt={proj.title}
                              width={size}
                              height={size}
                              decoding="async"
                              fetchpriority={idx < 8 ? 'high' : 'auto'}
                              loading="eager"
                              onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                          : proj.title.slice(0, 2).toUpperCase()
                        }
                      </IconCircle>
                      <Tooltip className="icon-tooltip">{proj.title}</Tooltip>
                    </IconWrap>
                  );
                })}
              </CloudContainer>
            </CloudWrap>
          )}
        </HeroTop>
      </Frame>
    </HeroSection>
  );
};

export default Hero;
